/**
 * notificationPriority — people first, then the system, folded.
 * (RW-2265, RW-2258 Phase 3b — F5.)
 *
 * The bell showed the newest ten of the base query regardless of type, so the
 * one recognition an agent received sat under a month of "Onboarding Reminder"
 * rows (48 unread = 45 nudges + 2 post reminders + 1 recognition on the seed
 * agent; one prod admin holds 846, 423 of them bug reports). Pure helpers so
 * NotificationBell and NotificationsPage order and fold the same way:
 *
 *   isPersonToPerson(n)          a real person caused it and it's a social type
 *   prioritizeNotifications(ns)  people rows first, then system rows with
 *                                consecutive same-type runs folded into one
 *                                expandable row ("3 overdue task reminders")
 *   foldSystemRuns(units, key)   the same fold over the inbox's thread units
 *   collapsedLabel(type, count)  the folded row's words
 */
import { categorize } from './notifications';

/** A notification a real person caused, of a social kind. */
export function isPersonToPerson(n) {
  if (!n || !n.fromUid || n.fromUid === 'system') return false;
  return categorize(n.type) === 'social';
}

const COLLAPSE_NOUNS = {
  task_overdue: 'overdue task reminders',
  overdue_items: 'overdue reminders',
  task_due: 'task reminders',
  task_assigned: 'task assignments',
  onboarding_nudge: 'onboarding reminders',
  onboarding_staff_nudge: 'staff onboarding nudges',
  onboarding_escalation: 'onboarding escalations',
  post_reminder: 'post reminders',
  staff_nudge: 'staff nudges',
  bug_report: 'bug reports',
  stale_review: 'stale reviews',
  event_reminder: 'event reminders',
  event_rsvp: 'RSVPs',
  info: 'updates',
  system: 'updates',
};

function prettyType(type) {
  return String(type || 'update').replace(/[_-]+/g, ' ').trim().toLowerCase();
}

/** "3 overdue task reminders" — the folded row's title. */
export function collapsedLabel(type, count) {
  const noun = COLLAPSE_NOUNS[type] || `${prettyType(type)} notifications`;
  return `${count} ${noun}`;
}

/**
 * Bell rows from a newest-first list. Person-to-person items keep their order
 * and come first; the rest keep their order, with consecutive runs of the same
 * type (2+) folded into one row.
 *
 * @returns {Array<{kind: 'item', n: object} | {kind: 'group', key: string, type: string, items: object[], latest: object, count: number}>}
 */
export function prioritizeNotifications(items) {
  const list = Array.isArray(items) ? items : [];
  const people = [];
  const system = [];
  list.forEach((n) => { if (isPersonToPerson(n)) people.push(n); else system.push(n); });
  const rows = people.map((n) => ({ kind: 'item', n }));
  let i = 0;
  while (i < system.length) {
    const type = system[i].type || 'info';
    let j = i + 1;
    while (j < system.length && (system[j].type || 'info') === type) j += 1;
    const run = system.slice(i, j);
    if (run.length >= 2) {
      rows.push({ kind: 'group', key: `run:${type}:${run[0].id}`, type, items: run, latest: run[0], count: run.length });
    } else {
      rows.push({ kind: 'item', n: run[0] });
    }
    i = j;
  }
  return rows;
}

/**
 * The inbox's version, over thread units ({ key, items }) in order: consecutive
 * single-item units of the same type (2+) fold into one unit marked `run`.
 * Real threads (2+ items sharing a source) are left alone.
 */
export function foldSystemRuns(units, scopeKey = '') {
  const list = Array.isArray(units) ? units : [];
  const out = [];
  let i = 0;
  while (i < list.length) {
    const u = list[i];
    const solo = u && Array.isArray(u.items) && u.items.length === 1;
    if (!solo) { out.push(u); i += 1; continue; }
    const type = u.items[0].type || 'info';
    let j = i + 1;
    while (j < list.length && list[j] && Array.isArray(list[j].items) && list[j].items.length === 1 && (list[j].items[0].type || 'info') === type) j += 1;
    if (j - i >= 2) {
      const items = list.slice(i, j).map((x) => x.items[0]);
      out.push({ key: `run:${scopeKey}:${type}:${items[0].id}`, items, run: type });
    } else {
      out.push(u);
    }
    i = j;
  }
  return out;
}
